/**
 * Formatting for the public marketing pages. Shared by the server render and
 * the browser bundle, so a countdown reads the same before and after the
 * script takes over. No Node or DOM APIs in here.
 *
 * Every date is printed in Europe/London, labelled BST or GMT, because the
 * sample data's clock is a London one (it starts at 25 Sep 2026 10:00 BST).
 */

const LONDON = 'Europe/London';

export const SYMBOL: Record<string, string> = {
  USD: '$',
  GBP: '£',
  EUR: '€',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  MYR: 'RM',
  BRL: 'R$',
};

function grouped(value: number): string {
  return Math.round(value).toLocaleString('en-GB');
}

/** "€2,498,000", "$904,300", "£10". */
export function money(amount: number, currency: string): string {
  return `${SYMBOL[currency] ?? ''}${grouped(amount)}`;
}

/** "€2.5M", "$1.24M", "£650k": a maximum or target on a card. */
export function short(amount: number, currency: string): string {
  const sym = SYMBOL[currency] ?? '';
  if (amount >= 1e6) return `${sym}${Math.round(amount / 1e4) / 100}M`;
  if (amount >= 1e3) return `${sym}${Math.round(amount / 10) / 100}k`;
  return `${sym}${grouped(amount)}`;
}

/** A whole number with thousands separators, for counts and stakes. */
export function num(value: number): string {
  return grouped(value);
}

/** The hero's big figure: "+40,000%". */
export function storyNumber(value: number): string {
  return `+${grouped(value)}%`;
}

/**
 * Percentage raised. Whole numbers, except that anything that would round up
 * to 100 keeps a decimal, so 99.92% never reads as "100%" while there is still
 * room under the cap.
 */
export function pct(value: number): string {
  if (value >= 99.5 && value < 100) return `${Math.floor(value * 10) / 10}%`;
  return `${Math.round(value)}%`;
}

function parts(
  ms: number,
  options: Intl.DateTimeFormatOptions,
  timeZone = LONDON,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-GB', {
    timeZone,
    ...options,
  }).formatToParts(ms)) {
    out[p.type] = p.value;
  }
  return out;
}

// Spelled out rather than taken from Intl: current CLDR abbreviates September
// in en-GB as "Sept", and Node and browsers ship different CLDR versions.
const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');
const WEEKDAYS = 'Sun Mon Tue Wed Thu Fri Sat'.split(' ');

/** The calendar date at that instant in the given zone. */
function date(ms: number, timeZone = LONDON) {
  const p = parts(
    ms,
    { year: 'numeric', month: 'numeric', day: 'numeric' },
    timeZone,
  );
  const [y, m, d] = [Number(p.year), Number(p.month), Number(p.day)];
  return {
    day: d,
    month: MONTHS[m - 1],
    year: y,
    weekday: WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()],
  };
}

/** "Sun 27 Sep". */
export function fD(ms: number): string {
  const p = date(ms);
  return `${p.weekday} ${p.day} ${p.month}`;
}

/** "Thu 17 Dec 2026". */
export function fDY(ms: number, timeZone = LONDON): string {
  const p = date(ms, timeZone);
  return `${p.weekday} ${p.day} ${p.month} ${p.year}`;
}

/** "24 Sep 2026", for provenance ("filed 24 Sep 2026"). */
export function fDS(ms: number): string {
  const p = date(ms);
  return `${p.day} ${p.month} ${p.year}`;
}

/** "BST" or "GMT", whichever London is on at that instant. */
export function zone(ms: number): string {
  return parts(ms, { timeZoneName: 'short' }).timeZoneName === 'GMT'
    ? 'GMT'
    : 'BST';
}

/** "10:00 BST". */
export function fT(ms: number): string {
  const p = parts(ms, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `${p.hour}:${p.minute} ${zone(ms)}`;
}

/** "Thu 24 Sep, 16:20 BST": when an alert went. */
export function fDT(ms: number): string {
  return `${fD(ms)}, ${fT(ms)}`;
}

/** "23:59", in the given zone: a US raise closes at 23:59 Eastern. */
export function hm(ms: number, timeZone = LONDON): string {
  const p = parts(
    ms,
    { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
    timeZone,
  );
  return `${p.hour}:${p.minute}`;
}

/** "4 min ago", "3 h ago", "2 d ago". */
export function ago(then: number, now: number): string {
  const min = Math.max(0, Math.floor((now - then) / 60_000));
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "2d 06:59:55". Never negative: a closed raise reads "0d 00:00:00". */
export function cd(msLeft: number): string {
  const s = Math.max(0, Math.floor(msLeft / 1000));
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d}d ${pad(h)}:${pad(m)}:${pad(s % 60)}`;
}

/**
 * "2d 7h", "5h", "12m": the coarse countdown, as the dashboard's
 * shortCountdown() writes it. Callers say "Closed" or "Ended" at zero.
 */
export function cdShort(msLeft: number): string {
  const h = Math.floor(msLeft / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(msLeft / 60_000))}m`;
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/**
 * What a screen reader hears for a countdown: "Closes in 2 days, 6 hours".
 * Coarse on purpose — it is the label of a timer that ticks every second, and
 * a label that changed every second would be read out every second.
 */
export function spoken(
  msLeft: number,
  verb: 'Closes' | 'Ends' = 'Closes',
): string {
  if (msLeft <= 0) return verb === 'Closes' ? 'Closed' : 'Ended';
  const h = Math.floor(msLeft / 3_600_000);
  const d = Math.floor(h / 24);
  const rest = h % 24;
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
  if (d === 0) {
    return h === 0
      ? `${verb} in under an hour`
      : `${verb} in ${plural(h, 'hour')}`;
  }
  return `${verb} in ${plural(d, 'day')}, ${plural(rest, 'hour')}`;
}
